// https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

// Returns the backend's message from an RTK Query error (deviceapi sends { ErrorMessage }, some
// handlers { message }), or the fallback when there isn't one (network failures, empty bodies).
export function getRtkErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null || !('data' in error)) return fallback;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return fallback;
  const { ErrorMessage, message } = data as { ErrorMessage?: unknown; message?: unknown };
  if (typeof ErrorMessage === 'string' && ErrorMessage.trim()) return ErrorMessage;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}
